import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { Helmet } from '@saas-pages/shared/public/seo'
import { BlogLanguageSwitcher, type BlogLocale } from '@saas-pages/shared/public/blog'
import { Seo, buildBlogPageMetadata } from '@saas-pages/shared/public/seo'
import branding from '../../../branding.config'
import {
  getAllPosts,
  getPostBySlug,
  type BlogPost,
  type RelatedToolKey,
} from '@/shared/blog/posts'
import { useLocale, useT } from '@/shared/i18n'

const BLOG_LOCALE_STORAGE_KEY = 'dateify.blog.locale'

interface BlogPostPageProps {
  slug: string
}

function formatDate(iso: string, locale: 'ru' | 'en'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function localeHref(post: BlogPost): Partial<Record<BlogLocale, string>> {
  return {
    [post.locale]: `/blog/${post.slug}`,
    ...Object.fromEntries(
      Object.entries(post.translations)
        .filter((entry): entry is [BlogLocale, string] => Boolean(entry[1]))
        .map(([locale, slug]) => [locale, `/blog/${slug}`]),
    ),
  }
}

function relatedToolData(key: RelatedToolKey, t: ReturnType<typeof useT>) {
  if (key === 'date-difference') {
    return {
      href: '/raznica-mezhdu-datami',
      icon: 'D',
      title: t.nav.dateDifference,
      description: t.blog.relatedToolDescription.dateDifference,
    }
  }

  if (key === 'time-zones') {
    return {
      href: '/chasovye-poyasa',
      icon: 'TZ',
      title: t.nav.timeZones,
      description: t.blog.relatedToolDescription.timeZones,
    }
  }

  return {
    href: '/timestamp',
    icon: 'T',
    title: t.nav.timestamp,
    description: t.blog.relatedToolDescription.timestamp,
  }
}

export function BlogPostPage({ slug }: BlogPostPageProps) {
  const t = useT()
  const { setRouteLocale } = useLocale()
  const post = getPostBySlug(slug)

  useEffect(() => {
    if (post) {
      setRouteLocale(post.locale)
      try {
        window.localStorage.setItem(BLOG_LOCALE_STORAGE_KEY, post.locale)
      } catch {
        // localStorage may be blocked by browser settings.
      }
      return () => setRouteLocale(null)
    }
  }, [post, setRouteLocale])

  if (!post) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex" />
          <title>{t.blog.notFoundTitle} - dateify.ru</title>
        </Helmet>
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-normal">{t.blog.notFoundTitle}</h1>
          <p className="mt-4 text-muted-foreground">
            {t.blog.notFoundDescription}
          </p>
          <Link to=".." className="mt-6 inline-block text-primary hover:underline">
            {t.common.allBlogPosts}
          </Link>
        </div>
      </>
    )
  }

  const metadata = buildBlogPageMetadata(post, getAllPosts(), {
    domain: branding.domain, brandName: branding.domain, defaultOgImage: branding.ogImage,
  })
  const PostContent = post.Component
  const relatedTools = post.relatedTools.map((key) => relatedToolData(key, t))
  const languageHrefs = localeHref(post)

  return (
    <>
      <Seo metadata={metadata} />

      <article className="mx-auto max-w-3xl">
        <nav aria-label={t.common.dateTools} className="mb-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            dateify.ru
          </Link>
          <span className="mx-2">/</span>
          <Link to=".." className="hover:text-foreground">
            {t.blog.breadcrumb}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{post.title}</span>
        </nav>

        <header className="mb-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md border bg-card px-2 py-0.5">
                {t.blog.localeName[post.locale]}
              </span>
              <time dateTime={post.date}>{formatDate(post.date, post.locale)}</time>
              {post.updated && post.updated !== post.date && (
                <>
                  <span aria-hidden>·</span>
                  <span>
                    {t.common.updated}:{' '}
                    <time dateTime={post.updated}>
                      {formatDate(post.updated, post.locale)}
                    </time>
                  </span>
                </>
              )}
            </div>
            <BlogLanguageSwitcher
              currentLocale={post.locale}
              ariaLabel={t.blog.postLanguageAria}
              labels={t.blog.localeName}
              hrefs={languageHrefs}
              disableMissingHrefs
              onSelectLocale={(next) => setRouteLocale(next)}
              renderLink={(_, href, className, children, onClick) => (
                <Link to={href} className={className} onClick={onClick}>
                  {children}
                </Link>
              )}
            />
          </div>
          <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">
            {post.title}
          </h1>
          <p className="mt-4 leading-7 text-muted-foreground">{post.description}</p>
        </header>

        <div className="prose prose-slate max-w-none prose-headings:tracking-normal prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none dark:prose-invert">
          <PostContent />
        </div>

        {post.faq.length > 0 && (
          <section className="mt-12 border-t pt-8">
            <h2 className="text-xl font-semibold tracking-normal">{t.common.faq}</h2>
            <div className="mt-4 space-y-4">
              {post.faq.map((item) => (
                <div key={item.q}>
                  <h3 className="font-medium tracking-normal">{item.q}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {relatedTools.length > 0 && (
          <section className="mt-12 rounded-lg border bg-muted/30 p-6">
            <h2 className="mb-4 text-base font-semibold tracking-normal">
              {t.blog.relatedTools}
            </h2>
            <ul className="space-y-3">
              {relatedTools.map((tool) => (
                <li key={tool.href}>
                  <Link
                    to={tool.href}
                    className="flex items-start gap-3 text-sm hover:text-primary"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background text-xs font-semibold">
                      {tool.icon}
                    </span>
                    <span>
                      <span className="font-medium">{tool.title}</span>
                      <span className="block text-xs leading-5 text-muted-foreground">
                        {tool.description}
                      </span>
                    </span>
                    <span className="ml-auto self-center text-xs text-muted-foreground">-&gt;</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-10 text-sm">
          <Link to=".." className="text-primary hover:underline">
            {t.common.allBlogPosts}
          </Link>
        </div>
      </article>
    </>
  )
}
